import { z } from "zod";
const personalDetailsSchema = z.object({
  nationality: z.string().nullable(),
  dateOfBirth: z.string().nullable(),
  placeOfBirth: z.string().nullable(),
  profession: z.string().nullable(),
  fiscalNumber: z.string().nullable(),
  idNumber: z.string().nullable(),
  passportNumber: z.string().nullable(),
  occupation: z.string().nullable(),
  martialStatus: z.string().nullable(),
  passportExpirationDate: z.string().nullable()
});
const createUserRequestSchema = z.object({
  user: z.object({
    email: z.string().email(),
    password: z.string(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    privacyConsent: z.boolean(),
    marketingConsent: z.boolean(),
    personalDetails: personalDetailsSchema.optional(),
    userProfileUuid: z.string().uuid().optional(),
    locale: z.string().optional()
  })
});
const createUserResponseSchema = z.object({
  user: z.object({
    id: z.number(),
    avatarImageUrl: z.string().optional(),
    countryCode: z.string().nullable(),
    email: z.string().email(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    privacyConsentAccepted: z.boolean(),
    marketingConsentAccepted: z.boolean(),
    userProfileUuid: z.string(),
    ahPlusMembership: z.boolean(),
    locale: z.string().nullable(),
    bankTransferDetails: z.unknown().optional(),
    chequeDetails: z.unknown().optional(),
    freeBankTransferDetails: z.unknown().optional(),
    mostRecentAddress: z.unknown().optional(),
    paypalDetails: z.unknown().optional(),
    userDetails: z.unknown().optional(),
    personalDetails: personalDetailsSchema.nullable(),
    phoneNumber: z.object({
      id: z.number(),
      number: z.string().nullable()
    })
  })
});
function createUser({
  client,
  params
}) {
  return client.url("/api/v3/users").snakeCaseBody(createUserRequestSchema.parse(params)).post().valid(createUserResponseSchema);
}
const fetchBrandRequestSchema = z.object({
  name: z.string()
});
const fetchBrandResponseSchema = z.object({
  brand: z.object({
    siteUrl: z.string().optional(),
    logoUrl: z.string(),
    name: z.string(),
    logoPresentInWebapp: z.boolean(),
    logoWidth: z.number(),
    primaryColor: z.string(),
    secondaryColor: z.string(),
    isExpedia: z.boolean(),
    isEgencia: z.boolean(),
    slug: z.string(),
    reloadPii: z.boolean()
  })
});
function fetchBrand({
  client,
  params
}) {
  const { name } = fetchBrandRequestSchema.parse(params);
  return client.get(`/api/brands/${name}`).valid(fetchBrandResponseSchema);
}
const freeBankTransferDetailsSchema = z.object({
  id: z.number().nullable(),
  accountType: z.string().nullable(),
  bankCountryCode: z.string().nullable(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  iban: z.string().nullable(),
  accountNumber: z.coerce.string().nullable(),
  swift: z.string().nullable(),
  routingNumber: z.coerce.string().nullable(),
  branchCode: z.coerce.string().nullable(),
  bsbCode: z.coerce.string().nullable(),
  sortCode: z.coerce.string().nullable(),
  bankAddress: z.string().nullable(),
  bankCode: z.coerce.string().nullable(),
  bankName: z.string().nullable(),
  address1: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  zip: z.string().nullable(),
  clabe: z.string().nullable(),
  beneficiaryIdentificationType: z.string().nullable(),
  beneficiaryIdentificationValue: z.string().nullable(),
  supportedCountries: z.array(z.string()),
  usStates: z.array(z.object({ stateCode: z.string(), name: z.string() })),
  caStates: z.array(z.object({ stateCode: z.string(), name: z.string() })),
  mxStates: z.array(z.object({ stateCode: z.string(), name: z.string() })),
  userId: z.number(),
  currency: z.string().nullable(),
  phoneNumber: z.coerce.string().nullable(),
  taxIdentifier: z.string().nullable()
});
const chequeDetailsSchema = z.object({
  id: z.number().nullable(),
  userId: z.number(),
  fullName: z.string().nullable(),
  address1: z.string().nullable(),
  address2: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  zip: z.string().nullable(),
  countryCode: z.string().nullable()
});
const bankTransferDetailsSchema = z.object({
  accountType: z.string(),
  address1: z.string().nullable(),
  address2: z.string().nullable(),
  bankCountryCode: z.string().nullable(),
  city: z.string().nullable(),
  comment: z.string().nullable(),
  countriesWithMandatoryIban: z.array(z.string()),
  countryCode: z.string().nullable(),
  firstName: z.string().nullable(),
  iban: z.string().nullable(),
  id: z.number().nullable(),
  lastName: z.string().nullable(),
  routingNumber: z.coerce.string().nullable(),
  state: z.string().nullable(),
  swift: z.string().nullable(),
  userId: z.number(),
  zip: z.string().nullable()
});
const claimAirportSchema = z.object({
  id: z.string().nullable(),
  iata: z.string(),
  ec261: z.boolean(),
  name: z.string(),
  countryCode: z.string(),
  city: z.string(),
  cityTranslations: z.object({
    en: z.string(),
    pl: z.string(),
    fr: z.string(),
    pt: z.string(),
    it: z.string(),
    el: z.string(),
    ru: z.string(),
    nb: z.string(),
    sv: z.string(),
    da: z.string(),
    de: z.string(),
    fi: z.string(),
    es: z.string(),
    nl: z.string(),
    "pt-BR": z.string(),
    "zh-CN": z.string()
  }).partial(),
  latitude: z.number(),
  longitude: z.number()
});
const claimAirlineSchema = z.object({
  id: z.string().nullable(),
  uniqueIdentifier: z.string().optional(),
  name: z.string(),
  iata: z.string(),
  icao: z.string(),
  code: z.string(),
  countryCode: z.string(),
  ec261: z.boolean()
});
const claimFlightSchema = z.object({
  id: z.number(),
  itineraryId: z.number(),
  flightNumber: z.string(),
  localDepartureDate: z.coerce.date(),
  distanceKm: z.number(),
  disrupted: z.boolean(),
  arrivalTime: z.coerce.date().nullable().optional(),
  departureTime: z.coerce.date().nullable().optional(),
  flightIdentifier: z.string().nullable(),
  departureAirportCode: z.string(),
  arrivalAirportCode: z.string(),
  marketingAirlineName: z.string(),
  operatingAirlineName: z.string().nullable(),
  delayMins: z.number().nullable().optional(),
  isCancelled: z.boolean().nullable().optional(),
  potentiallyMissedConnection: z.boolean().optional(),
  durationMins: z.number().optional(),
  departureScheduled: z.coerce.date().nullable().optional(),
  departureAirport: claimAirportSchema,
  arrivalAirport: claimAirportSchema,
  airline: claimAirlineSchema
});
const claimItinerarySchema = z.object({
  id: z.number(),
  links: z.object({
    travelDocuments: z.string().optional()
  }).optional(),
  flights: z.array(claimFlightSchema)
});
const fetchCurrentUserProfileResponseSchema = z.object({
  user: z.object({
    id: z.number(),
    countryCode: z.string().optional().nullable(),
    email: z.string().email(),
    firstName: z.string().optional().nullable(),
    lastName: z.string().optional().nullable(),
    privacyConsentAccepted: z.boolean(),
    marketingConsentAccepted: z.boolean(),
    userProfileUuid: z.string(),
    ahPlusMembership: z.boolean(),
    locale: z.string().optional().nullable(),
    phoneNumber: z.object({
      id: z.number(),
      number: z.string().nullable(),
      whatsappConsent: z.boolean().optional().nullable()
    }).optional().nullable(),
    bankTransferDetails: bankTransferDetailsSchema,
    chequeDetails: chequeDetailsSchema,
    freeBankTransferDetails: freeBankTransferDetailsSchema
  })
});
function fetchCurrentUserProfile({ client }) {
  return client.get("/api/users/me").valid(fetchCurrentUserProfileResponseSchema);
}
const fetchTrustpilotScoreResponseSchema = z.object({
  totalReviews: z.number(),
  scoreValue: z.number()
});
function fetchTrustpilotScore({ client }) {
  return client.get("/api/airhelp_score").valid(fetchTrustpilotScoreResponseSchema);
}
const fetchAirlinesRequestSchema = z.object({
  query: z.string()
});
const airlineSchema = z.object({
  uniqueIdentifier: z.string(),
  name: z.string(),
  alternativeName: z.string().optional().nullable(),
  displayName: z.string(),
  iata: z.string().nullable(),
  iataActive: z.boolean(),
  icao: z.string().nullable(),
  icaoActive: z.boolean(),
  code: z.string(),
  countryCode: z.string(),
  registrationCountryCode: z.string(),
  ec261: z.boolean(),
  active: z.boolean(),
  visible: z.boolean(),
  manuallyAdded: z.boolean()
});
const fetchAirlinesResponseSchema = z.object({
  airlines: z.array(airlineSchema)
});
function fetchAirlines({
  client,
  params
}) {
  const { query } = fetchAirlinesRequestSchema.parse(params);
  return client.options(
    {
      // Airlines endpoint requires us not to send auth cookies
      credentials: "omit"
    },
    true
  ).url("/api/airlines").query({ q: query }).get().valid(fetchAirlinesResponseSchema);
}
const fetchAirportsRequestSchema = z.object({
  query: z.string()
});
const airportSchema = z.object({
  searchText: z.string(),
  iata: z.string(),
  latitude: z.number(),
  longitude: z.number(),
  name: z.string(),
  city: z.string(),
  cityTranslations: z.record(z.string(), z.string()),
  country: z.string(),
  countryCode: z.string(),
  ec261: z.boolean(),
  rank: z.number(),
  timeZone: z.string()
});
const fetchAirportsResponseSchema = z.object({
  airports: z.array(airportSchema)
});
function fetchAirports({
  client,
  params
}) {
  const { query } = fetchAirportsRequestSchema.parse(params);
  return client.options(
    {
      // Airports endpoint requires us not to send auth cookies
      credentials: "omit"
    },
    true
  ).url(`/api/airports`).query({ q: query }).get().valid(fetchAirportsResponseSchema);
}
const confirmPayoutRequestSchema = z.object({
  claimId: z.string(),
  ahplusPlan: z.boolean(),
  purchaseMembership: z.boolean(),
  promoCode: z.string()
});
async function confirmPayout({
  params,
  client
}) {
  await client.url("/api/payout_confirmation").snakeCaseBody(confirmPayoutRequestSchema.parse(params)).post().res();
}
const createCreditCardRequestSchema = z.object({
  claimId: z.number(),
  userHasAhplusMembership: z.boolean(),
  purchaseAhplusMembership: z.boolean()
});
const createCreditCardTransferResponseSchema = z.object({
  creditCardTransferDetails: z.object({
    checkoutId: z.string()
  })
});
async function createCreditCardTransfer({
  client,
  params
}) {
  return client.url("/api/credit_card_transfer_details").snakeCaseBody(createCreditCardRequestSchema.parse(params)).post().valid(createCreditCardTransferResponseSchema);
}
const fetchCurrencyExchangeRateForTargetCountryRequestSchema = z.object({
  countryCode: z.string(),
  currencyCode: z.string()
});
const fetchCurrencyExchangeRateForTargetCountryTransferResponseSchema = z.object({
  currency: z.string().optional(),
  exchangeRate: z.number().optional()
});
async function fetchCurrencyExchangeRateForTargetCountry({
  client,
  params
}) {
  const { countryCode, currencyCode } = fetchCurrencyExchangeRateForTargetCountryRequestSchema.parse(params);
  return client.url(`/api/free_bank_transfer_details/exchange_currency_rate`).query({
    country_code: countryCode,
    currency: currencyCode
  }).get().valid(fetchCurrencyExchangeRateForTargetCountryTransferResponseSchema);
}
const fetchPaymentReferenceDataRequestSchema = z.object({
  countryCode: z.string(),
  sourceCurrency: z.string(),
  targetCurrency: z.string()
});
const fetchPaymentReferenceDataResponseSchema = z.object({
  payoutMethodType: z.string(),
  beneficiaryAddress: z.string(),
  beneficiaryCity: z.string(),
  beneficiaryStateOrProvince: z.string(),
  beneficiaryPostcode: z.string(),
  bankName: z.string(),
  bankCode: z.string(),
  bankCodes: z.record(z.string()),
  bankAddress: z.string(),
  branchCode: z.string(),
  aba: z.string(),
  acctNumber: z.string(),
  bsbCode: z.string(),
  sortCode: z.string(),
  beneficiaryIdentificationType: z.string(),
  beneficiaryIdentificationValue: z.string(),
  clabe: z.string(),
  iban: z.string(),
  bicSwift: z.string(),
  taxId: z.string(),
  phoneNumber: z.string(),
  accountType: z.string()
}).partial();
async function fetchPaymentReferenceData({
  client,
  params
}) {
  const { countryCode, sourceCurrency, targetCurrency } = fetchPaymentReferenceDataRequestSchema.parse(params);
  return client.url(`/api/payments_reference_data/${countryCode}`).query({
    source_currency: sourceCurrency,
    currency: targetCurrency
  }).get().valid(fetchPaymentReferenceDataResponseSchema);
}
const payoutOptions = [
  "bank_transfer",
  "charity",
  "cheque",
  "paypal",
  "payoneer",
  "free_bank_transfer",
  "credit_card_transfer"
];
const payoutOptionsPolicySchema = z.object({
  bankTransfer: z.boolean(),
  charity: z.boolean(),
  cheque: z.boolean(),
  paypal: z.boolean(),
  payoneer: z.boolean(),
  freeBankTransfer: z.boolean(),
  creditCardTransfer: z.boolean()
});
function getEnabledPayoutOptions(payoutOptionsPolicy) {
  return Object.entries(payoutOptionsPolicy).filter(([_, enabled]) => enabled).map(
    ([method]) => ({
      bankTransfer: "bank_transfer",
      charity: "charity",
      cheque: "cheque",
      paypal: "paypal",
      payoneer: "payoneer",
      freeBankTransfer: "free_bank_transfer",
      creditCardTransfer: "credit_card_transfer"
    })[method]
  );
}
const claimPayoutSchema = z.object({
  id: z.number(),
  collectedAt: z.coerce.date().nullable().optional(),
  currency: z.string(),
  customerReimbursementsInPreferredCurrency: z.string(),
  estimatedChequeDeliveryTime: z.string().optional(),
  finalCompensationInPreferredCurrency: z.string(),
  freeOfCharge: z.boolean().optional(),
  legalActionFeeAmountInPreferredCurrency: z.string(),
  legalAction: z.boolean().optional(),
  paid: z.boolean().optional(),
  isPaid: z.boolean().optional(),
  payoutCreated: z.boolean().optional(),
  payoutOptionsFees: z.object({
    bankTransfer: z.number(),
    charity: z.number(),
    cheque: z.number(),
    paypal: z.number(),
    payoneer: z.number(),
    freeBankTransfer: z.number(),
    manualPayout: z.number(),
    creditCardTransfer: z.number()
  }).optional(),
  payoutOptionsPolicy: payoutOptionsPolicySchema.optional(),
  status: z.string().optional(),
  payoutValue: z.string(),
  payoutValueInPreferredCurrency: z.string(),
  priceListVisible: z.boolean().optional(),
  processingFeeInPreferredCurrency: z.number().optional(),
  selectedPayoutOption: z.enum(payoutOptions).nullable().optional(),
  serviceFeeAmountInPreferredCurrency: z.string(),
  userExchangeCurrency: z.string().nullable().optional(),
  userExchangeRate: z.number().nullable().optional()
});
const claimAddressSchema = z.object({
  id: z.number(),
  address1: z.string(),
  address2: z.string().nullable(),
  city: z.string(),
  zip: z.string(),
  state: z.string(),
  countryCode: z.string(),
  formattedAddress: z.string()
});
const fetchPayoutClaimRequestSchema = z.object({
  claimId: z.number()
});
const fetchPayoutClaimResponseSchema = z.object({
  claim: z.object({
    id: z.number(),
    partnerSlug: z.string().nullable(),
    claimPayout: claimPayoutSchema,
    address: claimAddressSchema,
    itinerary: claimItinerarySchema
  })
}).transform(({ claim: { id, ...data } }) => {
  return {
    claimId: id,
    ...data
  };
});
async function fetchPayoutClaim({
  client,
  params
}) {
  const { claimId } = fetchPayoutClaimRequestSchema.parse(params);
  return client.url(`/api/claims/${claimId}`).query({ document_request_data: true }).get().valid(fetchPayoutClaimResponseSchema);
}
const postBankTransferDetailsRequestSchema = z.object({
  accountNumber: z.string().nullable(),
  address1: z.string().nullable(),
  autoCalculatedSwift: z.boolean(),
  bankCountryCode: z.string(),
  city: z.string().nullable(),
  claimId: z.string(),
  currency: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  payoutAmount: z.string(),
  sourceCurrency: z.string(),
  state: z.string().nullable(),
  zip: z.string().nullable(),
  accountType: z.string().nullable(),
  bankAddress: z.string().nullable(),
  bankCode: z.string().nullable(),
  bankName: z.string().nullable(),
  beneficiaryAddress: z.string().nullable(),
  beneficiaryIdentificationType: z.string().nullable(),
  beneficiaryIdentificationValue: z.string().nullable(),
  branchCode: z.string().nullable(),
  bsbCode: z.string().nullable(),
  clabe: z.string().nullable(),
  iban: z.string().nullable(),
  phoneNumber: z.string().nullable(),
  routingNumber: z.coerce.string().nullable(),
  sortCode: z.string().nullable(),
  supportedCountries: z.array(z.string()),
  swift: z.string().nullable(),
  taxIdentifier: z.string().nullable()
});
const postBankTransferDetailsResponseSchema = z.object({
  freeBankTransferDetails: freeBankTransferDetailsSchema
});
async function postBankTransferDetails({
  client,
  params
}) {
  const data = await client.url("/api/free_bank_transfer_details").snakeCaseBody({
    free_bank_transfer_details: postBankTransferDetailsRequestSchema.transform(({ address1, ...payload }) => {
      return {
        ...payload,
        address_1: address1
      };
    }).parse(params)
  }).post().valid(postBankTransferDetailsResponseSchema);
  return data.freeBankTransferDetails;
}
const postChequeDetailsRequestSchema = z.object({
  address1: z.string(),
  address2: z.string().nullable(),
  brandSlug: z.string().nullable(),
  city: z.string(),
  claimId: z.string(),
  countryCode: z.string(),
  fullName: z.string(),
  state: z.string(),
  userId: z.string(),
  zip: z.string()
});
const postChequeDetailsResponseSchema = z.object({
  chequeDetails: z.object({
    id: z.number(),
    userId: z.number(),
    fullName: z.string(),
    address1: z.string(),
    address2: z.string().nullable().optional(),
    city: z.string(),
    state: z.string(),
    zip: z.string(),
    countryCode: z.string()
  })
});
async function postChequeDetails({
  client,
  params
}) {
  return client.url(`/api/cheque_details`).snakeCaseBody(
    postChequeDetailsRequestSchema.transform(({ address1, address2, ...data }) => ({
      chequeDetails: {
        address_1: address1,
        address_2: address2,
        ...data
      }
    })).parse(params)
  ).post().valid(postChequeDetailsResponseSchema);
}
const postPaypalDetailsRequestSchema = z.object({
  email: z.string(),
  payoutLimit: z.number(),
  removeReceivingLimit: z.boolean(),
  userId: z.string()
});
const postPaypalDetailsResponseSchema = z.object({
  paypalDetails: z.object({
    id: z.number(),
    userId: z.number(),
    verified: z.boolean(),
    removedReceivingLimit: z.boolean(),
    payoutLimit: z.number()
  })
});
async function postPaypalDetails({
  client,
  params
}) {
  return client.url(`/api/paypal_details`).snakeCaseBody(
    postPaypalDetailsRequestSchema.transform((data) => ({
      paypalDetails: data
    })).parse(params)
  ).post().valid(postPaypalDetailsResponseSchema);
}
const putPayoutDetailsRequestSchema = z.object({
  payoutId: z.number(),
  claimPayout: z.object({
    claimId: z.string(),
    freeOfCharge: z.boolean().optional(),
    isPaid: z.boolean().optional(),
    selectedPayoutOption: z.string().optional(),
    serviceFeeAmountInPreferredCurrency: z.number().optional(),
    userExchangeCurrency: z.string().optional(),
    userExchangeRate: z.number().optional()
  })
});
async function putPayoutDetails({
  client,
  params
}) {
  const { payoutId, claimPayout } = putPayoutDetailsRequestSchema.parse(params);
  await client.url(`/api/claim_payouts/${payoutId}`).snakeCaseBody({ claimPayout }).put().res();
}
export {
  fetchPaymentReferenceData as A,
  getEnabledPayoutOptions as B,
  claimPayoutSchema as C,
  claimAddressSchema as D,
  fetchPayoutClaimRequestSchema as E,
  fetchPayoutClaim as F,
  postBankTransferDetailsRequestSchema as G,
  postBankTransferDetailsResponseSchema as H,
  postBankTransferDetails as I,
  postChequeDetailsRequestSchema as J,
  postChequeDetails as K,
  postPaypalDetailsRequestSchema as L,
  postPaypalDetails as M,
  putPayoutDetailsRequestSchema as N,
  putPayoutDetails as O,
  createUserResponseSchema as a,
  createUser as b,
  createUserRequestSchema as c,
  fetchBrandResponseSchema as d,
  fetchBrand as e,
  fetchBrandRequestSchema as f,
  fetchCurrentUserProfileResponseSchema as g,
  fetchCurrentUserProfile as h,
  fetchTrustpilotScoreResponseSchema as i,
  fetchTrustpilotScore as j,
  fetchAirlinesRequestSchema as k,
  airlineSchema as l,
  fetchAirlinesResponseSchema as m,
  fetchAirlines as n,
  fetchAirportsRequestSchema as o,
  airportSchema as p,
  fetchAirportsResponseSchema as q,
  fetchAirports as r,
  confirmPayoutRequestSchema as s,
  confirmPayout as t,
  createCreditCardRequestSchema as u,
  createCreditCardTransfer as v,
  fetchCurrencyExchangeRateForTargetCountryRequestSchema as w,
  fetchCurrencyExchangeRateForTargetCountry as x,
  fetchPaymentReferenceDataRequestSchema as y,
  fetchPaymentReferenceDataResponseSchema as z
};
//# sourceMappingURL=put-payout-details-B6POuPIV.js.map
